import { Input, Output, SimpleChanges, EventEmitter, Component, OnInit, OnChanges } from '@angular/core';

import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { IUser } from 'projects/common/src/app/interfaces/user';
import { ParameterHandlerService } from 'projects/common/src/app/services';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { IStatus } from 'src/app/modules/core/interfaces';
import { GoogleAnalyticsService } from 'src/app/modules/core/services/google/google-analytics.service';
import { StatusService } from 'src/app/modules/core/services/parameter';
import { IMachineStatus } from 'src/app/modules/widget/interfaces/machine-status';

@Component({
  selector: 'app-change-status',
  templateUrl: './change-status.component.html',
  styleUrls: ['./change-status.component.scss'],
})
export class ChangeStatusComponent implements OnInit, OnChanges {
  @Output() public updateStatusMachine = new EventEmitter<any>();
  @Input() public status: any;

  public machine: IMachineStatus;
  public statusImage: string;
  public availableStatus: IStatus[];
  public statusId: number;
  public formStatus: FormGroup;
  public selectedStatus: any;
  public message: string;
  public statusActual: number;
  public disabledChangeStatus: boolean;
  public hasMessage = false;
  public hasError = false;

  constructor(
    public activeModal: NgbActiveModal,
    private statusService: StatusService,
    private fb: FormBuilder,
    private parameterHandler: ParameterHandlerService,
    private languageService: LanguageService,
    private googleAnalyticsService: GoogleAnalyticsService,
  ) {
    this.availableStatus = [];
    this.disabledChangeStatus = true;
  }

  ngOnInit() {
    this.initializeForm();
    this.loadInitialStatus();
    this.subscribeToStatusChanges();
    this.trackAnalyticsEvent();
    this.getAvailableStatus();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.status && changes.status.currentValue) {
      this.machine = changes.status.currentValue;
      this.statusActual = this.machine.statusId;
      this.selectedStatus = { statusId: this.statusActual };

      if (this.formStatus) {
        this.formStatus.controls.selectedStatus.setValue(this.machine.statusId);
      }
    }
  }

  /**
   * Initializes the form with default values and validators
   */
  private initializeForm(): void {
    this.formStatus = this.fb.group({
      selectedStatus: new FormControl(this.machine.statusId, Validators.required),
      comment: new FormControl({ value: '', disabled: true }, Validators.required),
    });
  }

  /**
   * Loads the initial status of the machine and sets it in the form if available
   */
  private loadInitialStatus(): void {
    if (this.status && this.status.statusId != null) {
      this.machine = this.status;
      this.statusActual = this.machine.statusId;
      this.formStatus.controls.selectedStatus.setValue(this.statusActual);
    } else {
      console.warn('El estado inicial está vacío o no tiene un statusId');
    }
  }

  /**
   * Subscribes to changes in the selected status control to enable or disable comment and button
   */
  private subscribeToStatusChanges(): void {
    this.formStatus.get('selectedStatus').valueChanges.subscribe((value) => {
      this.selectedStatus = value;
      const isSameStatus = this.statusActual === this.selectedStatus;
      this.disabledChangeStatus = isSameStatus;
      this.formStatus.controls.comment[isSameStatus ? 'disable' : 'enable']();
    });
  }

  /**
   * Tracks a custom analytics event for the modal
   */
  private trackAnalyticsEvent(): void {
    const customEvent = {
      modal_widget: 'asarco_state',
      tab_modal: 'asarco_state_change-status',
    };
    this.googleAnalyticsService.sendCustomEvent('modal', customEvent);
  }

  /**
   * Function to load the status options on select
   */
  public getAvailableStatus(): void {
    this.statusService
      .getAllStatus()
      .toPromise()
      .then((result) => {
        const status = result;
        this.availableStatus = status;

        setTimeout(() => {
          this.formStatus.controls.selectedStatus.setValue(this.machine.statusId);
        }, 1000);
      });
  }

  /**
   * Function to select an status from select
   *
   * @param $event Selected item
   */
  public selectStatus($event): void {
    this.selectedStatus = $event;

    const childrenStatus: IStatus[] = [];
    for (const status of this.availableStatus) {
      for (const child of status.childs) {
        childrenStatus.push(child);
        if (child.hasChildren) {
          for (const grandchild of child.childs) {
            childrenStatus.push(grandchild);
          }
        }
      }
    }

    // Actualizar imagen de estado según el estado seleccionado
    const newStatus = childrenStatus.find((value) => Number(value.statusId) === this.selectedStatus.statusId);
    this.statusImage = newStatus?.statusImage ?? this.statusImage;
    this.statusId = this.selectedStatus?.statusId;

    // Activar/desactivar el campo de comentarios y el botón según el cambio de estado
    if (this.statusActual === this.selectedStatus.statusId) {
      this.disabledChangeStatus = true;
      this.formStatus.controls.comment.disable();
    } else {
      this.disabledChangeStatus = false;
      this.formStatus.controls.comment.enable();
    }
  }

  /**
   * Update Status machine
   */
  public updateStatus(): void {
    if (!this.selectedStatus || this.selectedStatus.statusId == null) {
      console.warn('No se puede actualizar, el estado seleccionado es inválido');
      return;
    }

    const user = this.parameterHandler.parameters.item('user-data').value as IUser;

    const params = {
      machineId: Number(this.machine.machineId),
      statusId: Number(this.selectedStatus.statusId),
      userId: Number(user.id),
      operatorId: 0,
      comment: String(this.formStatus.get('comment').value || ''),
    };

    this.statusService.updateStatusInsert(params).subscribe({
      next: (data) => {
        if (data.error.errorNumber === 0) {
          this.message = this.languageService.message('status change successfull');
          this.formStatus.controls.comment.disable();
          this.disabledChangeStatus = true;

          // Reasignar el estado actual después de la actualización
          this.statusActual = this.selectedStatus.statusId;
          this.statusId = this.statusActual;
          this.selectedStatus = { statusId: this.statusActual, statusImage: this.selectedStatus.statusImage || 'default' };

          this.updateStatusMachine.emit(this.selectedStatus);
          this.formStatus.reset({
            selectedStatus: this.statusActual,
            comment: '',
          });
          this.hasMessage = true;
          this.hasError = false;
        } else {
          this.hasMessage = true;
          this.hasError = true;
        }
      },
      error: (error) => {
        console.error(error);
        this.hasMessage = true;
        this.hasError = true;
        this.message = this.languageService.message('status change error');
      },
    });
  }

  public clear(): void {
    this.formStatus.controls.selectedStatus.setValue(this.machine.statusId);
    this.formStatus.controls.comment.setValue('');
    this.formStatus.controls.comment.disable();
    this.hasMessage = false;
    this.selectedStatus = this.statusActual;
    this.disabledChangeStatus = true;
  }
}
